import { createRouter, createWebHashHistory, START_LOCATION } from "vue-router";
import Account from "./views/Account.vue";
import Login from "./views/Login.vue";
import Signup from "./views/Signup.vue";
import Plan from "./views/Plan.vue";
import Links from "./views/Links.vue";
import Home from "./views/Home.vue";
import TermsOfService from "./views/TermsOfService.vue";
import PrivacyPolicy from "./views/PrivacyPolicy.vue";
import { auth } from "./auth";
import store from "./store";
import Processing from "./views/Processing.vue";
import AlmostDone from "./views/AlmostDone.vue";
import Integrations from "./views/Integrations.vue";
import VerifyEmailAddress from "./views/VerifyEmailAddress.vue";
import DeepLinking from "./views/DeepLinking.vue";
import Team from "./views/Team.vue";
import Invite from "./views/Invite.vue";
import { capture } from "./analytics";
import { getPendingInvitation } from "./pendingInvitation";

// define all internal paths->component mapping
const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: "/login",
      name: "login",
      component: Login,
      meta: {
        requiresAuth: false,
        removeBanner: true,
      },
    },
    {
      path: "/signup",
      name: "signup",
      component: Signup,
      meta: {
        requiresAuth: false,
        removeBanner: true,
      },
    },
    {
      path: "/invite/:token",
      name: "invite",
      component: Invite,
      meta: {
        requiresAuth: false,
        removeBanner: true,
      },
    },
    {
      path: "/",
      name: "home",
      component: Home,
    },
    {
      path: "/overview",
      redirect: "/",
    },
    {
      path: "/processing",
      name: "processing",
      component: Processing,
    },
    {
      path: "/domains",
      name: "domains",
      // lazy: keeps the page and its Public Suffix List (tldts, ~46 KB gz)
      // out of the main bundle
      component: () => import("./views/Domains.vue"),
    },
    {
      path: "/integrations",
      name: "integrations",
      component: Integrations,
    },
    {
      path: "/team",
      name: "team",
      component: Team,
    },
    {
      path: "/deep-linking",
      name: "deep-linking",
      component: DeepLinking,
    },
    {
      path: "/account",
      name: "account",
      component: Account,
    },
    {
      path: "/plan",
      name: "plan",
      component: Plan,
    },
    {
      path: "/links",
      name: "links",
      component: Links,
    },
    {
      path: "/almost-done",
      name: "almost-done",
      component: AlmostDone,
    },
    {
      path: "/verify-email",
      name: "verify-email",
      component: VerifyEmailAddress,
    },
    {
      path: "/terms-of-service",
      name: "terms-of-service",
      component: TermsOfService,
      meta: {
        requiresAuth: false,
        removeBanner: true,
      },
    },
    {
      path: "/privacy-policy",
      name: "privacy-policy",
      component: PrivacyPolicy,
      meta: {
        requiresAuth: false,
        removeBanner: true,
      },
    },
  ],
});

export const guard = (to) => {
  if (store.state.isLoading === false) {
    store.commit("setIsLoading", true);
  }

  // no route matches (e.g. a stale/typo'd link): there's no view to mount and
  // clear the loading overlay raised above, so it would hang forever.
  if (to.matched.length === 0) {
    return "/";
  }

  const currentUser = auth().currentUser;

  const requiresAuth = to.matched.some((record) => {
    if (record.meta && typeof record.meta.requiresAuth !== "undefined") {
      return record.meta.requiresAuth;
    }
    return true;
  });
  const isAuthPath = to.name === "login" || to.name === "signup";

  console.log("path requires auth", requiresAuth);
  console.log("is auth path", isAuthPath);

  if (requiresAuth && !currentUser && !isAuthPath) {
    console.log("redirecting to login");
    const headedPath = window.location.hash.substr(1);
    const query =
      headedPath && headedPath !== "/" ? { from: headedPath } : null;
    return { name: "login", query };
  }

  // identifiers only: the full firebase user carries stsTokenManager, and session
  // replay records the console, so logging it leaks live refresh tokens
  console.log("currentUser", {
    uid: currentUser?.uid,
    email: currentUser?.email,
    emailVerified: currentUser?.emailVerified,
    to: to.path,
  });

  if (isAuthPath && currentUser) {
    return "/";
  }

  // an invitation opened before signing in: back to it once the person can use the app,
  // never in the middle of the verification steps
  const pending = getPendingInvitation();
  if (
    currentUser &&
    pending &&
    store.getters.isEmailVerified2022 &&
    !["invite", "verify-email", "almost-done"].includes(to.name)
  ) {
    return { name: "invite", params: { token: pending.token } };
  }

  return true;
};

router.beforeEach(guard);

// the guard above raises the loading overlay on every navigation and only the
// destination view lowers it again — so a navigation that never reaches a view
// (aborted by a dialog's back-button handler, superseded, duplicated, or
// thrown) would leave the overlay covering the whole app for good. That also
// covers a redirect (e.g. an unmatched route above, sent to "/") that
// resolves back to the route already on screen: vue-router reuses the
// existing component instance rather than remounting it, so its created()
// hook -- the only thing that otherwise lowers the overlay -- never re-runs.
//
// Not on the initial navigation, though: the router's start location has
// path "/", so a fresh load of / would count as same-path and drop the
// overlay before Home.vue has mounted, let alone loaded its data.
const clearLoading = () => store.commit("setIsLoading", false);
router.afterEach((to, from, failure) => {
  if (failure || (to.path === from.path && from !== START_LOCATION)) {
    clearLoading();
  }
});

// posthog-js sends a $pageview only on a full page load. Its history_change
// mode doesn't help here either: it compares location.pathname, which hash
// routes never change (always "/"). So every in-app navigation is sent from
// here, once vue-router has written the new URL, which $current_url reads.
// Not the initial navigation: the page load already sent that one
export function trackPageview(to, from, failure) {
  if (failure || from === START_LOCATION) return;
  capture("$pageview");
}
router.afterEach(trackPageview);

// lazy routes (Domains) load a hashed chunk on first visit, and each Netlify
// deploy is atomic, so a tab opened before a deploy asks for a chunk that no
// longer exists and the navigation fails. Vite reports that with this event;
// flag the error so onError can tell it apart from any other failure
window.addEventListener("vite:preloadError", (event) => {
  if (event.payload) event.payload.isChunkLoadError = true;
});

const CHUNK_RELOAD_KEY = "chunk-reload-at";
router.onError((err, to) => {
  clearLoading();
  if (!err?.isChunkLoadError) return;
  // reload into the new build, on the page the user was opening: the URL only
  // changes after the chunk resolves, so a plain reload would land them back
  // where they clicked from. Not again right after a reload: a chunk still
  // missing then is a real error, and reloading again would loop
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
    if (Date.now() - lastReload < 10000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return; // no sessionStorage: cannot guard against a loop, do not reload
  }
  window.history.replaceState(null, "", router.resolve(to).href);
  window.location.reload();
});

export default router;
